import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  ProjectMetadataFields,
  type MetadataValues,
} from './ProjectMetadataFields'

interface CreateProjectFormProps {
  name: string
  slug: string
  description: string
  metadata: MetadataValues
  error: string
  isLoading: boolean
  onNameChange: (v: string) => void
  onSlugChange: (v: string) => void
  onDescriptionChange: (v: string) => void
  onMetadataChange: (next: MetadataValues) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
}

export function CreateProjectForm({
  name,
  slug,
  description,
  metadata,
  error,
  isLoading,
  onNameChange,
  onSlugChange,
  onDescriptionChange,
  onMetadataChange,
  onSubmit,
  onCancel,
}: CreateProjectFormProps) {
  return (
    <form
      onSubmit={onSubmit}
      className="bg-surface border border-border-subtle rounded-xl p-6 space-y-5"
    >
      <div>
        <h2 className="font-display text-lg font-semibold text-text">New project</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          A workspace for one product, customer, or engagement.
        </p>
      </div>

      {error && (
        <div className="px-3 py-2 bg-danger-bg text-danger-fg text-sm rounded-lg">{error}</div>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-[2fr_1fr] gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              autoFocus
              className="w-full h-9 px-3 bg-warm-50 border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
              placeholder="My product"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) =>
                onSlugChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
              }
              className="w-full h-9 px-3 bg-warm-50 border border-border rounded-lg text-sm font-mono text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
              placeholder="my-product"
              pattern="[a-z0-9-]+"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
            Description{' '}
            <span className="text-text-muted normal-case font-normal">— optional</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            className="w-full px-3 py-2 bg-warm-50 border border-border rounded-lg text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15 resize-none"
            placeholder="What is this project about?"
            rows={2}
          />
        </div>
      </div>

      <div className="pt-3 border-t border-border-subtle">
        <p className="text-xs text-text-muted mb-3 uppercase tracking-wide font-medium">
          Portfolio metadata
        </p>
        <ProjectMetadataFields values={metadata} onChange={onMetadataChange} />
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            'Create project'
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
